import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';

/**
 * SmsSendOtpOutputComponent — shown after SMS Send OTP succeeds.
 *
 * Replaces the generic JSON-dump output for THIS flow only. Surfaces the
 * client_id (the long opaque string we need to pair with the user's typed
 * OTP on the verify step), with two affordances that the generic output
 * doesn't give us:
 *
 *   1. A one-click Copy button for the client_id (it's a long string;
 *      manual selection is annoying)
 *   2. A "Verify OTP now →" button that navigates to the submit-OTP page
 *      with the client_id pre-filled via react-router location state.
 *      Cuts a 4-click flow (open sidebar → click Submit OTP → paste id →
 *      type otp) down to 1 click + typing the OTP.
 *
 * Falls back to a tiny JSON preview if Surepass returned no client_id
 * (would only happen on a malformed response — keeps the page useful
 * even when something weird happens upstream).
 */
const SmsSendOtpOutputComponent = ({ reportData }) => {
    const navigate = useNavigate();
    const [copied, setCopied] = useState(false);

    // The controller now surfaces client_id at the top level (see PR
    // SmsSendOtpController.php). Fall back to deeper paths for safety.
    const response = reportData?.response || reportData || {};
    const clientId = response.client_id
        || response?.data?.data?.client_id
        || response?.data?.client_id;

    const copyClientId = async () => {
        if (!clientId) return;
        try {
            await navigator.clipboard.writeText(clientId);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API blocked (e.g. non-HTTPS) — fall back to manual selection.
        }
    };

    // The Submit OTP page route. The :serviceId for SMS Submit is 56 in
    // current seed data — keep this in sync with the menu service if it
    // ever changes. The route is also registered in App.js (see grep).
    const goToVerify = () => {
        navigate('/app/reports/surepass-sms-submit-otp/56', {
            state: { prefillClientId: clientId },
        });
    };

    if (!clientId) {
        return (
            <div className="p-3 surface-100 border-round">
                <div className="text-sm text-color-secondary mb-2">
                    SMS sent, but the response didn't include a client_id —
                    Surepass response shape may have changed. Raw payload:
                </div>
                <pre className="text-xs overflow-auto" style={{ maxHeight: '200px' }}>
                    {JSON.stringify(response, null, 2)}
                </pre>
            </div>
        );
    }

    return (
        <div className="surface-card border-1 surface-border border-round p-4">
            <div className="flex align-items-center gap-2 mb-3">
                <i className="pi pi-check-circle text-green-500 text-2xl" aria-hidden="true"></i>
                <div>
                    <div className="text-lg font-bold text-color">OTP sent successfully</div>
                    <div className="text-sm text-color-secondary">
                        Check your phone — the SMS should arrive within 30 seconds.
                    </div>
                </div>
            </div>

            <div className="mb-3">
                <label className="block text-xs uppercase font-semibold text-color-secondary mb-2"
                       style={{ letterSpacing: '.05em' }}>
                    Client ID (needed to verify the OTP)
                </label>
                <div className="flex align-items-center gap-2 flex-wrap">
                    <code
                        className="surface-50 border-1 surface-border border-round p-2 text-sm flex-grow-1"
                        style={{ fontFamily: 'monospace', wordBreak: 'break-all', minWidth: '0' }}
                    >
                        {clientId}
                    </code>
                    <Button
                        icon={copied ? 'pi pi-check' : 'pi pi-copy'}
                        label={copied ? 'Copied' : 'Copy'}
                        onClick={copyClientId}
                        severity={copied ? 'success' : 'secondary'}
                        outlined
                        size="small"
                    />
                </div>
            </div>

            <div className="flex gap-2 flex-wrap mt-3">
                <Button
                    label="Verify OTP now →"
                    icon="pi pi-shield"
                    onClick={goToVerify}
                />
                <Tag
                    value="OTP valid for ~10 min"
                    icon="pi pi-clock"
                    severity="info"
                    rounded
                />
            </div>
        </div>
    );
};

export default SmsSendOtpOutputComponent;
